import { useState, useEffect } from 'react'
import {X, Info} from 'lucide-react'
import type { Vacancy } from '@/hooks/useVacancies'

const DAYS: { key: string; label: string }[] = [
  { key: 'Lun', label: 'Lunes' },
  { key: 'Mar', label: 'Martes' },
  { key: 'Mié', label: 'Miércoles' },
  { key: 'Jue', label: 'Jueves' },
  { key: 'Vie', label: 'Viernes' },
  { key: 'Sáb', label: 'Sábado' },
  { key: 'Dom', label: 'Domingo' },
]

interface DaySchedule {
  enabled: boolean
  from: string
  to: string
}

type ScheduleMap = Record<string, DaySchedule>

const buildEmptySchedule = (): ScheduleMap =>
  DAYS.reduce((acc, d) => {
    acc[d.key] = { enabled: false, from: '', to: '' }
    return acc
  }, {} as ScheduleMap)

// Convierte el mapa de horarios a un texto legible: "Lun 09:00-18:00, Mar 09:00-14:00"
function scheduleToString(map: ScheduleMap): string {
  return DAYS.filter((d) => map[d.key]?.enabled)
    .map((d) => {
      const { from, to } = map[d.key]
      if (from && to) return `${d.key} ${from}-${to}`
      if (from) return `${d.key} desde ${from}`
      return d.key
    })
    .join(', ')
}

// Parsea un texto previamente guardado de vuelta al mapa estructurado
function stringToSchedule(text?: string): ScheduleMap {
  const map = buildEmptySchedule()
  if (!text) return map
  text.split(',').forEach((part) => {
    const trimmed = part.trim()
    if (!trimmed) return
    const dayKey = DAYS.find((d) => trimmed.startsWith(d.key))?.key
    if (!dayKey) return
    map[dayKey].enabled = true
    const rest = trimmed.slice(dayKey.length).trim()
    const range = rest.match(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/)
    if (range) {
      map[dayKey].from = range[1]
      map[dayKey].to = range[2]
    } else {
      const single = rest.match(/(\d{1,2}:\d{2})/)
      if (single) map[dayKey].from = single[1]
    }
  })
  return map
}

function ScheduleSelector({ value, onChange }: { value: ScheduleMap; onChange: (m: ScheduleMap) => void }) {
  const toggle = (key: string) => {
    onChange({ ...value, [key]: { ...value[key], enabled: !value[key].enabled } })
  }
  const setTime = (key: string, field: 'from' | 'to', v: string) => {
    onChange({ ...value, [key]: { ...value[key], [field]: v } })
  }
  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
      {DAYS.map((d) => {
        const day = value[d.key]
        return (
          <div key={d.key} className="flex flex-wrap items-center gap-2">
            <label className="flex w-28 items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={day.enabled}
                onChange={() => toggle(d.key)}
                className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
              />
              {d.label}
            </label>
            {day.enabled ? (
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={day.from}
                  onChange={(e) => setTime(d.key, 'from', e.target.value)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                />
                <span className="text-xs text-slate-400">a</span>
                <input
                  type="time"
                  value={day.to}
                  onChange={(e) => setTime(d.key, 'to', e.target.value)}
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400"
                />
              </div>
            ) : (
              <span className="text-xs italic text-slate-400">Sin horario</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function FieldLabel({ text, hint, required }: { text: string; hint: string; required?: boolean }) {
  return (
    <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-600">
      <span>
        {text}
        {required ? ' *' : ''}
      </span>
      <span className="group relative inline-flex">
        <Info size={13} className="cursor-help text-slate-400 hover:text-amber-500" />
        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 w-52 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-[11px] font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
          {hint}
        </span>
      </span>
    </label>
  )
}

interface VacancyFormModalProps {
  open: boolean
  initial?: Partial<Vacancy> | null
  onClose: () => void
  onSubmit: (data: Partial<Vacancy>) => Promise<void>
}

const empty: Partial<Vacancy> = {
  title: '',
  area: '',
  salaryMin: undefined,
  salaryMax: undefined,
  contractType: '',
  schedule: '',
  modality: 'Presencial',
  benefits: '',
  requirements: '',
  activities: '',
  experience: '',
  education: '',
  skills: [],
  city: '',
  state: '',
  closeDate: '',
}

function VacancyFormModal({ open, initial, onClose, onSubmit }: VacancyFormModalProps) {
  const [form, setForm] = useState<Partial<Vacancy>>(empty)
  const [skillsText, setSkillsText] = useState('')
  const [schedule, setSchedule] = useState<ScheduleMap>(buildEmptySchedule())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open) {
      const data = initial ? { ...empty, ...initial } : empty
      setForm(data)
      setSkillsText((data.skills || []).join(', '))
      setSchedule(stringToSchedule(data.schedule))
    }
  }, [open, initial])

  if (!open) return null

  const set = (k: keyof Vacancy, v: any) => setForm((p) => ({ ...p, [k]: v }))

  const handleSubmit = async () => {
    if (!form.title?.trim()) {
      window.alert('El puesto es obligatorio')
      return
    }
    setSaving(true)
    try {
      const skills = skillsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const payload: Partial<Vacancy> = {
        ...form,
        skills,
        schedule: scheduleToString(schedule),
        salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
        salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
        closeDate: form.closeDate || undefined,
      }
      await onSubmit(payload)
      onClose()
    } catch (e) {
      console.error('Error al guardar vacante:', e)
      window.alert('Ocurrió un error al guardar la vacante. Inténtalo de nuevo.')
    } finally {
      setSaving(false)
    }
  }

  const inputCls =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-bold text-slate-900">
            {initial?._id ? 'Editar vacante' : 'Publicar vacante'}
          </h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <FieldLabel text="Puesto" required hint="Nombre del cargo o vacante que ofreces. Ej. Desarrollador Frontend, Vendedor, Contador." />
              <input className={inputCls} value={form.title || ''} onChange={(e) => set('title', e.target.value)} placeholder="Ej. Desarrollador Frontend" />
            </div>
            <div>
              <FieldLabel text="Área" hint="Departamento o área de la empresa al que pertenece el puesto. Ej. Ventas, Sistemas, Recursos Humanos." />
              <input className={inputCls} value={form.area || ''} onChange={(e) => set('area', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Modalidad" hint="Forma de trabajo: Presencial (en sitio), Remota (a distancia) o Híbrida (combinación de ambas)." />
              <select className={inputCls} value={form.modality || 'Presencial'} onChange={(e) => set('modality', e.target.value)}>
                <option value="Presencial">Presencial</option>
                <option value="Remota">Remota</option>
                <option value="Hibrida">Híbrida</option>
              </select>
            </div>
            <div>
              <FieldLabel text="Salario mínimo" hint="Monto mensual más bajo que ofreces para el puesto, en pesos. Se mostrará como rango salarial." />
              <input type="number" className={inputCls} value={form.salaryMin ?? ''} onChange={(e) => set('salaryMin', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Salario máximo" hint="Monto mensual más alto que estás dispuesto a ofrecer, en pesos. Junto al mínimo define el rango." />
              <input type="number" className={inputCls} value={form.salaryMax ?? ''} onChange={(e) => set('salaryMax', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Tipo de contratación" hint="Esquema de contrato. Ej. Tiempo completo, Medio tiempo, Por proyecto, Temporal, Honorarios." />
              <input className={inputCls} value={form.contractType || ''} onChange={(e) => set('contractType', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Horario" hint="Selecciona los días laborales y define la hora de entrada y salida de cada uno. Cada día puede tener un horario diferente." />
              <ScheduleSelector value={schedule} onChange={setSchedule} />
            </div>
            <div>
              <FieldLabel text="Ciudad" hint="Ciudad donde se ubica el empleo o desde donde se coordina si es remoto. Ej. Guadalajara." />
              <input className={inputCls} value={form.city || ''} onChange={(e) => set('city', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Estado" hint="Estado o entidad federativa de la vacante. Ej. Jalisco, Nuevo León, CDMX." />
              <input className={inputCls} value={form.state || ''} onChange={(e) => set('state', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Experiencia requerida" hint="Años o tipo de experiencia previa que necesita el candidato. Ej. 2 años en puestos similares." />
              <input className={inputCls} value={form.experience || ''} onChange={(e) => set('experience', e.target.value)} />
            </div>
            <div>
              <FieldLabel text="Escolaridad requerida" hint="Nivel de estudios mínimo solicitado. Ej. Bachillerato, Licenciatura trunca o concluida, Posgrado." />
              <input className={inputCls} value={form.education || ''} onChange={(e) => set('education', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Habilidades" hint="Competencias técnicas o blandas necesarias, separadas por coma. Ej. React, Comunicación, Excel." />
              <input className={inputCls} value={skillsText} onChange={(e) => setSkillsText(e.target.value)} placeholder="React, TypeScript, Comunicación" />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Fecha de cierre" hint="Último día para recibir postulaciones. Después de esta fecha la vacante deja de aceptar candidatos." />
              <input type="date" className={inputCls} value={(form.closeDate || '').slice(0, 10)} onChange={(e) => set('closeDate', e.target.value ? new Date(e.target.value).toISOString() : '')} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Actividades a desempeñar" hint="Describe las tareas y responsabilidades principales del puesto en el día a día." />
              <textarea rows={2} className={inputCls} value={form.activities || ''} onChange={(e) => set('activities', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Requisitos" hint="Condiciones indispensables para el puesto. Ej. licencia de conducir, disponibilidad de viajar, idiomas." />
              <textarea rows={2} className={inputCls} value={form.requirements || ''} onChange={(e) => set('requirements', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel text="Prestaciones ofrecidas" hint="Beneficios adicionales al salario. Ej. seguro de gastos médicos, vales, bonos, días extra de vacaciones." />
              <textarea rows={2} className={inputCls} value={form.benefits || ''} onChange={(e) => set('benefits', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-lg bg-slate-900 px-5 py-2 text-sm font-semibold text-amber-400 transition hover:bg-slate-800 disabled:opacity-60"
          >
            {saving ? 'Guardando...' : initial?._id ? 'Guardar cambios' : 'Publicar vacante'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default VacancyFormModal
